import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiCheckCircle, FiClock, FiMail, FiPhone, FiUser, FiUsers, FiXCircle } from 'react-icons/fi';
import { httpsCallable } from 'firebase/functions';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { functions } from '../firebase';
import layoutStyles from '../styles/layout.module.css';
import styles from './ArtistApplicationsAdmin.module.css';

const FILTERS = ['pending', 'approved', 'rejected', 'all'];

function formatTimestamp(timestamp = {}) {
  if (!timestamp?.millis) return 'Not available';
  return new Date(timestamp.millis).toLocaleString();
}

function statusTone(status) {
  if (status === 'approved') return styles.statusApproved;
  if (status === 'rejected') return styles.statusRejected;
  return styles.statusPending;
}

export default function ArtistApplicationsAdmin() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('pending');
  const [applications, setApplications] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [internalNote, setInternalNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadApplications = useCallback(async (nextFilter = statusFilter, preferredId = '') => {
    try {
      setLoading(true);
      setError('');
      const callable = httpsCallable(functions, 'listArtistAccessApplications');
      const response = await callable({ status: nextFilter });
      const nextApplications = Array.isArray(response?.data?.applications) ? response.data.applications : [];
      setApplications(nextApplications);
      const resolvedId = preferredId && nextApplications.some((item) => item.id === preferredId)
        ? preferredId
        : nextApplications[0]?.id || '';
      setSelectedId(resolvedId);
      const selected = nextApplications.find((item) => item.id === resolvedId);
      setInternalNote(selected?.internalNote || '');
    } catch (err) {
      setApplications([]);
      setSelectedId('');
      setInternalNote('');
      setError(err?.message || 'Unable to load artist applications.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadApplications(statusFilter);
  }, [loadApplications, statusFilter]);

  const selectedApplication = useMemo(
    () => applications.find((item) => item.id === selectedId) || null,
    [applications, selectedId]
  );

  useEffect(() => {
    setInternalNote(selectedApplication?.internalNote || '');
  }, [selectedApplication?.id, selectedApplication?.internalNote]);

  const handleReview = useCallback(async (action) => {
    if (!selectedApplication?.id) return;
    try {
      setActionBusy(action);
      setError('');
      setNotice('');
      const callable = httpsCallable(functions, 'reviewArtistAccessApplication');
      await callable({
        applicationId: selectedApplication.id,
        action,
        internalNote,
      });
      setNotice(action === 'approve' ? 'Application approved.' : 'Application rejected.');
      await loadApplications(statusFilter, selectedApplication.id);
    } catch (err) {
      setError(err?.message || 'Unable to save the application review.');
    } finally {
      setActionBusy('');
    }
  }, [internalNote, loadApplications, selectedApplication, statusFilter]);

  return (
    <div className={layoutStyles.detailPage}>
      <TopBar variant="back" backLabel="Back" onBack={() => navigate('/admin')} rightActionLabel="Home" onRightAction={() => navigate('/home')} />
      <div className={`${layoutStyles.detailMain} ${styles.pageShell}`.trim()}>
        <header className={styles.header}>
          <span className={styles.eyebrow}>Admin</span>
          <h1>Artist Applications</h1>
          <p>Review submitted artist applications, keep internal notes, and approve or reject applicants from one queue.</p>
        </header>

        <section className={styles.filterRow}>
          {FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              className={`${styles.filterButton} ${statusFilter === filter ? styles.filterButtonActive : ''}`.trim()}
              onClick={() => setStatusFilter(filter)}
              disabled={loading}
            >
              {filter}
            </button>
          ))}
        </section>

        {(notice || error) ? (
          <section className={styles.noticeCard}>
            {notice ? <p className={styles.noticeText}>{notice}</p> : null}
            {error ? <p className={styles.errorText}>{error}</p> : null}
          </section>
        ) : null}

        <section className={styles.workspace}>
          <section className={styles.queueCard}>
            <div className={styles.sectionHeader}>
              <div>
                <span className={styles.sectionEyebrow}>Queue</span>
                <h2>Applications</h2>
              </div>
              <span className={styles.countBadge}>{applications.length}</span>
            </div>

            {loading ? <p className={styles.state}>Loading artist applications…</p> : null}
            {!loading && !applications.length ? (
              <div className={styles.emptyState}>
                <strong>No applications in this filter.</strong>
                <p>{statusFilter === 'pending' ? 'No pending artist applications are waiting for review.' : 'Try another filter to review earlier decisions.'}</p>
              </div>
            ) : null}

            {applications.length ? (
              <div className={styles.queueList}>
                {applications.map((application) => (
                  <button
                    key={application.id}
                    type="button"
                    className={`${styles.queueItem} ${selectedId === application.id ? styles.queueItemActive : ''}`.trim()}
                    onClick={() => setSelectedId(application.id)}
                  >
                    <div className={styles.queueTop}>
                      <strong>{application.artistOrStageName || application.fullName || 'Unnamed applicant'}</strong>
                      <span className={`${styles.statusPill} ${statusTone(application.status)}`.trim()}>{application.status}</span>
                    </div>
                    <p>{application.fullName || 'No legal name provided'}</p>
                    <span className={styles.queueMeta}>{formatTimestamp(application.submittedAt)}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </section>

          <section className={styles.detailCard}>
            <div className={styles.sectionHeader}>
              <div>
                <span className={styles.sectionEyebrow}>Detail</span>
                <h2>Selected Application</h2>
              </div>
            </div>

            {!selectedApplication ? (
              <div className={styles.emptyState}>
                <strong>No application selected.</strong>
                <p>Choose an application from the queue to review its details.</p>
              </div>
            ) : (
              <div className={styles.detailBody}>
                <div className={styles.identityCard}>
                  <div>
                    <span className={styles.sectionEyebrow}>Artist / Stage Name</span>
                    <h3>{selectedApplication.artistOrStageName || 'Not provided'}</h3>
                    <p>{selectedApplication.fullName || 'No full name provided'}</p>
                  </div>
                  <span className={`${styles.statusPill} ${statusTone(selectedApplication.status)}`.trim()}>{selectedApplication.status}</span>
                </div>

                <div className={styles.metaGrid}>
                  <div className={styles.metaItem}>
                    <span><FiUser /> Applicant UID</span>
                    <strong>{selectedApplication.uid || 'Not available'}</strong>
                  </div>
                  <div className={styles.metaItem}>
                    <span><FiClock /> Submitted</span>
                    <strong>{formatTimestamp(selectedApplication.submittedAt)}</strong>
                  </div>
                  <div className={styles.metaItem}>
                    <span><FiMail /> Email</span>
                    <strong>{selectedApplication.email || 'Not provided'}</strong>
                  </div>
                  <div className={styles.metaItem}>
                    <span><FiPhone /> Phone</span>
                    <strong>{selectedApplication.phone || 'Not provided'}</strong>
                  </div>
                  <div className={styles.metaItem}>
                    <span><FiUsers /> Agent / Manager</span>
                    <strong>{selectedApplication.hasAgent ? 'Yes' : 'No'}</strong>
                  </div>
                  <div className={styles.metaItem}>
                    <span><FiClock /> Best Contact</span>
                    <strong>{selectedApplication.bestContactMethod || 'Not provided'}</strong>
                  </div>
                </div>

                {selectedApplication.hasAgent ? (
                  <section className={styles.block}>
                    <h3>Agent / Manager Contact</h3>
                    <div className={styles.metaGrid}>
                      <div className={styles.metaItem}>
                        <span>Name</span>
                        <strong>{selectedApplication.agentName || 'Not provided'}</strong>
                      </div>
                      <div className={styles.metaItem}>
                        <span>Email</span>
                        <strong>{selectedApplication.agentEmail || 'Not provided'}</strong>
                      </div>
                      <div className={styles.metaItem}>
                        <span>Phone</span>
                        <strong>{selectedApplication.agentPhone || 'Not provided'}</strong>
                      </div>
                    </div>
                  </section>
                ) : null}

                <section className={styles.block}>
                  <h3>Social Links</h3>
                  <p>{selectedApplication.socialLinksText || 'No social links were submitted.'}</p>
                </section>

                <section className={styles.block}>
                  <h3>Applicant Notes</h3>
                  <p>{selectedApplication.notes || 'No notes were submitted.'}</p>
                </section>

                <section className={styles.block}>
                  <h3>Review Metadata</h3>
                  <div className={styles.metaGrid}>
                    <div className={styles.metaItem}>
                      <span>Reviewed At</span>
                      <strong>{formatTimestamp(selectedApplication.reviewedAt)}</strong>
                    </div>
                    <div className={styles.metaItem}>
                      <span>Reviewed By UID</span>
                      <strong>{selectedApplication.reviewedByUid || 'Not reviewed yet'}</strong>
                    </div>
                    <div className={styles.metaItem}>
                      <span>Reviewed By Email</span>
                      <strong>{selectedApplication.reviewedByEmail || 'Not reviewed yet'}</strong>
                    </div>
                  </div>
                </section>

                <section className={styles.block}>
                  <h3>Internal Note</h3>
                  <textarea
                    value={internalNote}
                    onChange={(event) => setInternalNote(event.target.value)}
                    rows={5}
                    placeholder="Optional internal note for this review."
                  />
                  <div className={styles.actionRow}>
                    <button
                      type="button"
                      className={styles.approveButton}
                      onClick={() => handleReview('approve')}
                      disabled={Boolean(actionBusy)}
                    >
                      <FiCheckCircle />
                      {actionBusy === 'approve' ? 'Approving…' : 'Approve'}
                    </button>
                    <button
                      type="button"
                      className={styles.rejectButton}
                      onClick={() => handleReview('reject')}
                      disabled={Boolean(actionBusy)}
                    >
                      <FiXCircle />
                      {actionBusy === 'reject' ? 'Rejecting…' : 'Reject'}
                    </button>
                  </div>
                </section>
              </div>
            )}
          </section>
        </section>
      </div>
    </div>
  );
}
